import { Button } from '../button/button';
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import { ReactComponent as PainLocationsIcon } from "../../assets/icons/solid/broken-heart-icon.svg";
import { ReactComponent as QuestionMarkIcon } from "../../assets/icons/solid/question-mark.svg";
import { ReactComponent as AiIcon } from "../../assets/icons/symmio-icons/ai-icon.svg";
import { ReactComponent as DownloadIcon } from "../../assets/icons/general-icons/download.svg";
import { ReactComponent as SparklesIcon } from "../../assets/icons/general-icons/sparkles.svg";
import { ReactComponent as CloseIcon } from "../../assets/icons/general-icons/close.svg";
import DonutChart from "../charts/donut-chart";
import RiskChartVertical from "../charts/risk-chart-vertical";
import { SymmioAccessType } from "../../models/enumerations/symmio-access-type";
import FocusAreaCard from "../focus-areas-card/focus-areas-card";
import GrayFooterCard from "../gray-footer-card/gray-footer-card";
import MskScoreUtil from "../../utilities/msk-score-util";
import { MskScore } from '../../models/interfaces/msk-score';
import AssessmentResponse from '../../models/interfaces/assessment-response.entity';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import AppContentUtil from '../../utilities/app-content-util';
import WellnessProgramService from '../../utilities/services/wellness-program-service';
import { WellnessProgram } from '../../models/interfaces/wellness-program';
import UserUtil from '../../utilities/user-util';
import { Modal } from '../modal/modal';
import ReportDataUtil from '../../utilities/report-data-util';
import DataAssessmentsUtil from '../../utilities/data-assesments-util';
import { AiResponseStatus, MskScoreSummaryOutput } from '../../models/interfaces/ai-response';
import AiResponseService from '../../utilities/services/ai-response-service';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import CardButton from '../card-button/card-button';
import { createLogger } from '../../utilities/logger';

const log = createLogger("AssessmentResultsHeader");
const COMPONENT_CLASS = "c-assessment-results";
const QUESTION_MARK_VIDEO_URL = "";

interface AssessmentResultsHeaderProps {
    mskScore?: MskScore;
    latestAssessmentResponses?: AssessmentResponse[];
    riskChartPercentage: number;
    lastAssessment: string;
    lastAssessmentDateInDays: string;
}

const AssessmentResultsHeader: React.FC<AssessmentResultsHeaderProps> = (props: AssessmentResultsHeaderProps) => {
    const {
        mskScore,
        riskChartPercentage,
        latestAssessmentResponses,
        lastAssessment,
        lastAssessmentDateInDays,
    } = props;

    const { state } = useAuthState();
    const { t } = useTranslation();
    const history = useHistory();
    const [openRetestModal, setOpenRetestModal] = useState(false);
    const [wellnessProgram, setWellnessProgram] = useState<WellnessProgram>();

    const [showQuestionMarkModal, setShowQuestionMarkModal] = useState(false);
    const questionMarkVideoEmbed = AppContentUtil.getVideoEmbedCode(QUESTION_MARK_VIDEO_URL);

    const [showSummarySection, setShowSummarySection] = useState(false);
    const [summaryData, setSummaryData] = useState<MskScoreSummaryOutput | null>(null);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [summaryError, setSummaryError] = useState(false);
    const [isLiked, setIsLiked] = useState<boolean | null>(null);
    const [copied, setCopied] = useState(false);
    const summaryUnsubscribeRef = useRef<(() => void) | null>(null);
    const aiResponseRef = useRef<any>(null);
    const lastOutputRef = useRef<string>("");
    const beats = summaryData
        ? [
            { label: `${t("assessmentResults.aiSummary.mskScoreBenchmark")}`, text: summaryData.beatOneMskScoreBenchmark, color: "#E7514F" },
            summaryData.beatThreePainSummary ? { label: `${t("assessmentResults.aiSummary.painFindings")}`, text: summaryData.beatThreePainSummary, color: "#43B5F8" } : null,
            { label: `${t("assessmentResults.aiSummary.riskCategory")}`, text: summaryData.beatTwoMskRiskSummary, color: "#FFC79C" },
            { label: `${t("assessmentResults.aiSummary.focusAreaSummary")}`, text: summaryData.beatFourFocusAreasSummary, color: "#00DFBC" },
            { label: `${t("assessmentResults.aiSummary.conclusion")}`, text: summaryData.beatFiveClosingInsight, color: "#ADDFFD" },
        ].filter(Boolean) as { label: string; text: string; color: string }[]
        : [];

    const fullSummaryText = beats.map((b) => b.text).join("\n\n");

    const handleCloseSummarySection = useCallback(() => {
        if (summaryUnsubscribeRef.current) {
            summaryUnsubscribeRef.current();
            summaryUnsubscribeRef.current = null;
        }
        setShowSummarySection(false);
        setSummaryData(null);
        setSummaryLoading(false);
        setSummaryError(false);
        setIsLiked(null);
        lastOutputRef.current = "";
    }, []);

    const handleViewSummary = useCallback(() => {
        if (!mskScore?.aiResponseId) return;

        if (showSummarySection) {
            handleCloseSummarySection();
            return;
        }

        setSummaryLoading(true);
        setSummaryError(false);
        setSummaryData(null);
        setIsLiked(null);
        setShowSummarySection(true);

        const unsubscribe = AiResponseService.getSnapshot(mskScore.aiResponseId, (aiResponse: any) => {
            if (!aiResponse) {
                setSummaryLoading(false);
                setSummaryError(true);
                return;
            }

            aiResponseRef.current = aiResponse;
            const status = aiResponse.status as AiResponseStatus;

            if (status === AiResponseStatus.Complete) {
                setSummaryLoading(false);
                setSummaryError(false);
                const outputJson = JSON.stringify(aiResponse.output);
                if (outputJson !== lastOutputRef.current) {
                    lastOutputRef.current = outputJson;
                    setSummaryData(aiResponse.output as MskScoreSummaryOutput);
                }
                setIsLiked(aiResponse.isLiked ?? null);
            } else if (status === AiResponseStatus.Error || status === AiResponseStatus.QuotaExceeded) {
                setSummaryLoading(false);
                setSummaryError(true);
            }
        });

        summaryUnsubscribeRef.current = unsubscribe;
    }, [mskScore?.aiResponseId, showSummarySection, handleCloseSummarySection]);

    const handleCopySummary = () => {
        if (!fullSummaryText) return;

        navigator.clipboard.writeText(fullSummaryText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const handleLike = async (liked: boolean) => {
        const newValue = isLiked === liked ? null : liked;
        setIsLiked(newValue);

        if (aiResponseRef.current?.id) {
            try {
                await AiResponseService.setFeedback(aiResponseRef.current, newValue);
            } catch {
                setIsLiked(isLiked);
            }
        }
    };

    const reportCardIcon = <DownloadIcon />;
    const summaryCardIcon = <AiIcon />;

    useEffect(() => {
        const getWellnessProgram = async () => {
            if (state.user && state.user.wellnessProgramId) {
                let fetchedWellnessProgram = await WellnessProgramService.get(state.user.wellnessProgramId);

                if (fetchedWellnessProgram) {
                    setWellnessProgram(fetchedWellnessProgram);
                }
            }
        }

        if (!wellnessProgram) {
            getWellnessProgram();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.user])

    return (
        <div>
            <div className="py-6 flex flex-wrap justify-center gap-5">
                <div className={`${COMPONENT_CLASS}__score-section-card`}>
                    <h3 className="mb-4">
                        {t('dashboard.overallScore')}
                    </h3>
                    <div className={`${COMPONENT_CLASS}__donut-chart`}>
                        <DonutChart
                            percentage={mskScore?.percentage}
                            title={t('userProfile.wellnessScore')}
                        />
                    </div>
                    {mskScore &&
                        <div className="text-xs text-center font-light my-4">
                            {DataAssessmentsUtil.getMskAverage(mskScore)}
                        </div>
                    }
                    {(state.user?.isSuperAdmin || !state.user?.wellnessProgramId || !wellnessProgram || !wellnessProgram.isCurrent || state.user.symmioAccess !== SymmioAccessType.AppLicense || state.user?.administeredRetake) &&
                        <div>
                            <Button
                                buttonStyle="secondary"
                                buttonText={t('assessmentResults.retest')}
                                onClick={() => {
                                    if (!state.user?.wellnessProgramId || !wellnessProgram || !wellnessProgram.isCurrent) {
                                        history.push("/movement?retake=true");
                                    }
                                    else {
                                        setOpenRetestModal(true);
                                    }
                                }} />
                        </div>
                    }
                </div>

                <div className="flex flex-col gap-5">
                    <div className={`${COMPONENT_CLASS}__score-section-card`}>
                        <h3 className="flex gap-3 items-center justify-center mb-4">
                            {t('assessmentResults.mskHealth')}
                            {questionMarkVideoEmbed &&
                                <div className='cursor-pointer' onClick={() => setShowQuestionMarkModal(true)}>
                                    <QuestionMarkIcon />
                                </div>
                            }
                        </h3>
                        <div className="w-full h-64">
                            <RiskChartVertical
                                heightPercentage={100 - riskChartPercentage}
                                score={mskScore?.riskFactor?.percentage}
                                breakpoints={[
                                    { min: 0, max: 0, label: t('riskFactor.optimal'), bgColor: "#8BE3CE" },
                                    { min: 1, max: 27, label: t('riskFactor.acceptable'), bgColor: "#ADDFFD" },
                                    { min: 28, max: 72, label: t('riskFactor.low'), bgColor: "#FFC79C" },
                                    { min: 73, max: 100, label: t('riskFactor.veryLow'), bgColor: "#FFA998" },
                                ]}
                                decimals={false}
                            />
                        </div>
                    </div>

                    <div className={`${COMPONENT_CLASS}__score-section-card`}>
                        <div className="w-fit flex gap-1 items-center justify-center">
                            <div>
                                <PainLocationsIcon />
                            </div>
                            <div className="font-bold text-2xl">
                                {mskScore && latestAssessmentResponses &&
                                    MskScoreUtil.getNumberOfPainLocations(mskScore, latestAssessmentResponses)
                                }
                            </div>
                            <div className="uppercase font-bold text-10" dangerouslySetInnerHTML={{ __html: t('assessmentResults.focus.painIdentified') }}>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col gap-5">
                    {mskScore && mskScore.focusAreas && mskScore.focusAreas.length > 0 &&
                        <FocusAreaCard focusAreas={mskScore.focusAreas} title={t('assessmentResults.areasOfFocus')} />
                    }

                    <GrayFooterCard mainText={lastAssessment} subText={t('assessmentResults.lastAssessment')} footerText={lastAssessmentDateInDays} />
                </div>

                <Modal
                    isOpen={openRetestModal}
                    isLoading={false}
                    onClose={setOpenRetestModal}
                    defaultModalActions={true}
                    onSubmit={() => {
                        if (wellnessProgram && state.user && state.user.id && mskScore) {
                            log.info("Retake initiated", {
                                wellnessProgramId: wellnessProgram.id,
                                mskScoreId: mskScore.id,
                            });
                            UserUtil.handleRetestClick(state.user, wellnessProgram, mskScore).then(() => {
                                setWellnessProgram(undefined);
                                history.push("/movement?retake=true");
                            }).catch((e) => {
                                log.error("Failed to initiate retake", {
                                    exception: e,
                                    wellnessProgramId: wellnessProgram?.id,
                                    mskScoreId: mskScore?.id,
                                });
                            })
                        }
                    }}
                    onCancel={() => setOpenRetestModal(false)}
                    title={t('assessmentResults.retest_modal.title')}
                    submitButtonText={t('general.yes')}
                    cancelButtonText={t('buttons.btn_cancel')}
                >
                    <div className="mb-6">
                        <p className="text-sm font-light mb-4" dangerouslySetInnerHTML={{ __html: t('assessmentResults.retest_modal.text') }}></p>
                    </div>
                </Modal>

                {questionMarkVideoEmbed &&
                    <Modal
                        isOpen={showQuestionMarkModal}
                        isLoading={false}
                        onClose={setShowQuestionMarkModal}>
                        <div className={`${COMPONENT_CLASS}__video-container-modal`}>
                            <div dangerouslySetInnerHTML={{ __html: questionMarkVideoEmbed }}></div>
                        </div>
                    </Modal>
                }
            </div>
            <div className={`${COMPONENT_CLASS}__report-section`}>
                <div className="flex items-center justify-center gap-4">
                    {mskScore?.aiResponseId && (
                        <div style={{ width: "calc(50% - 0.5rem)" }}>
                            <CardButton
                                height={52}
                                icon={summaryCardIcon}
                                label={showSummarySection ? t('buttons.btn_hideAiSummary') : t('buttons.btn_viewAiSummary')}
                                onClick={handleViewSummary}
                            />
                        </div>
                    )}
                    <div style={{ width: "calc(50% - 0.5rem)" }}>
                        <CardButton
                            height={52}
                            icon={reportCardIcon}
                            label={t('buttons.btn_downloadYourReport')}
                            onClick={() => ReportDataUtil.downloadWellness(state.user, mskScore)}
                        />
                    </div>

                </div>
            </div>

            <div>
                <p className="italic text-center" style={{ fontSize: "12px" }}>{t('assessmentResults.disablePopUp')}</p>
            </div>

            {
                showSummarySection &&
                <div className={`${COMPONENT_CLASS}__summary-section mt-6 p-6 rounded-2xl border border-black-gray-5 bg-white/90`} style={{ boxShadow: "2px 4px 25px 0 rgba(0, 0, 0, 0.07)" }}>
                    <div className="flex justify-between items-center -mx-6 -mt-6 px-6 py-6 rounded-t-2xl bg-black-gray-5">
                        <div className="flex items-center gap-2">
                            <SparklesIcon className="w-5 h-5" />
                            <span className="label-small font-medium">{t('assessmentResults.aiSummary.title')}</span>
                        </div>
                        <button onClick={handleCloseSummarySection} className="flex items-center gap-1">
                            <span className="label-small">{t('buttons.btn_close')}</span>
                            <CloseIcon className="w-4 h-4" />
                        </button>
                    </div>
                    {summaryLoading && (
                        <div className="flex flex-col items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mb-4" />
                            <p className="small-text text-gray-500">{t('assessmentResults.aiSummary.generatingSummary')}</p>
                        </div>
                    )}

                    {!summaryLoading && summaryError && (
                        <div className="flex flex-col items-center justify-center py-8">
                            <p className="small-text text-red-500">
                                {t('assessmentResults.aiSummary.unableToGenerate')}
                            </p>
                        </div>
                    )}

                    {!summaryLoading && !summaryError && !summaryData && (
                        <div className="flex flex-col items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mb-4" />
                            <p className="small-text text-gray-500">{t('assessmentResults.aiSummary.waitingForSummary')}</p>
                        </div>
                    )}

                    {!summaryLoading && !summaryError && summaryData && (
                        <div style={{ marginTop: "1rem" }}>
                            {beats.map((beat, i) => (
                                <div key={i} className={i < beats.length - 1 ? "mb-4" : ""}>
                                    <h4 className="body-bold mb-1" style={{ borderLeft: `2px solid ${beat.color}`, paddingLeft: "0.5rem" }}>{beat.label}</h4>
                                    <p className="body whitespace-pre-wrap" style={{ paddingLeft: "calc(0.5rem + 2px)" }}>{beat.text}</p>
                                </div>
                            ))}

                            <div className="flex justify-between items-center gap-3 pt-4 mt-4 -mx-6 px-6 border-t border-black-gray-5">
                                <div className="flex items-center gap-2">
                                    <SparklesIcon className="w-4 h-4" />
                                    <span className="small-text">{t('assessmentResults.aiSummary.aiMessage')}</span>
                                </div>

                                <div className="flex items-center gap-1">
                                    <button
                                        className="flex items-center gap-1 px-3 py-2 rounded-md transition-colors"
                                        onClick={handleCopySummary}
                                    >
                                        {copied
                                            ? <CheckIcon fontSize="small" />
                                            : <ContentCopyIcon fontSize="small" />
                                        }
                                    </button>

                                    <button
                                        className="flex items-center gap-1 px-3 py-2 rounded-md transition-colors"
                                        onClick={() => handleLike(true)}
                                    >
                                        {isLiked === true
                                            ? <ThumbUpIcon fontSize="small" />
                                            : <ThumbUpOutlinedIcon fontSize="small" />
                                        }
                                    </button>

                                    <button
                                        className="flex items-center gap-1 px-3 py-2 rounded-md transition-colors"
                                        onClick={() => handleLike(false)}
                                    >
                                        {isLiked === false
                                            ? <ThumbDownIcon fontSize="small" />
                                            : <ThumbDownOutlinedIcon fontSize="small" />
                                        }
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            }
        </div >
    );
}

export default AssessmentResultsHeader;
